/**
 * Presence wire contracts — the UI side of the SignalR presence events published by the
 * Einstein API (src/Galileo.Einstein.Api/Common/Realtime/PresenceRealtimeEvents.cs and
 * WorkflowHub.Presence.cs).
 *
 * Parse-don't-validate at the subscription boundary: every inbound payload is parsed
 * through a Zod schema before any downstream code sees it. Unknown entity types or
 * malformed shapes are rejected here and dropped, never surfaced as an unsanctioned
 * discriminator. New entity types are additive — extend the enum in lockstep with the API.
 */

import { z } from "zod";

/** Server->client event names. Stable strings; mirror PresenceRealtimeEvents on the API. */
export const PRESENCE_EVENTS = {
    memberActive: "PresenceMemberActive",
    memberLeft: "PresenceMemberLeft",
    syncRequested: "PresenceSyncRequested",
} as const;

/** Client->server hub methods. Mirror WorkflowHub.Presence. */
export const PRESENCE_METHODS = {
    join: "JoinPresence",
    announce: "AnnouncePresence",
    leave: "LeavePresence",
    watch: "WatchPresence",
    stopWatching: "StopWatchingPresence",
} as const;

/**
 * How a UI surface holds a record reference:
 * - `view` — the user is actively on the record (counts as present; broadcast to others).
 * - `watch` — the surface only observes the roster (e.g. a list row); never counted present.
 */
export type PresenceHold = "view" | "watch";

/**
 * Heartbeat / TTL constants. The roster is eventually-consistent live state computed on the
 * client; heartbeat + TTL is the structural compensation for missed leaves (instance crash,
 * dropped messages). TTL is deliberately > 2x the heartbeat so a single missed beat never
 * evicts a still-present viewer. These also govern the convergence window for list watchers:
 * a fresh watcher learns the current roster on the next heartbeat from each present viewer.
 */
export const PRESENCE_HEARTBEAT_MS = 20_000;
export const PRESENCE_TTL_MS = 50_000;
export const PRESENCE_PRUNE_INTERVAL_MS = 10_000;

/**
 * Feature flag mirroring the Galileo rollout switch. Presence is advisory, so it defaults on
 * and is disabled only by an explicit `VITE_PRESENCE_ENABLED=false`, letting the channel be
 * killed via config without a code change.
 */
export const PRESENCE_ENABLED: boolean = ((): boolean => {
    const raw = import.meta.env["VITE_PRESENCE_ENABLED"] as string | undefined;
    return raw === undefined || raw.toLowerCase() !== "false";
})();

export const presenceEntityTypeSchema = z.enum(["workflow", "workItem"]);
export type PresenceEntityType = z.infer<typeof presenceEntityTypeSchema>;

export const presenceMemberSchema = z.object({
    galileoUserId: z.string().min(1),
    displayName: z.string().min(1),
    // Initials may legitimately be empty for odd display names; do not reject.
    initials: z.string(),
    shortLabel: z.string().min(1),
    connectionId: z.string().min(1),
});
export type PresenceMember = z.infer<typeof presenceMemberSchema>;

export const presenceMemberActiveSchema = z.object({
    entityType: presenceEntityTypeSchema,
    entityId: z.string().min(1),
    member: presenceMemberSchema,
    occurredAt: z.string(),
});
export type PresenceMemberActive = z.infer<typeof presenceMemberActiveSchema>;

export const presenceMemberLeftSchema = z.object({
    entityType: presenceEntityTypeSchema,
    entityId: z.string().min(1),
    connectionId: z.string().min(1),
    occurredAt: z.string(),
});
export type PresenceMemberLeft = z.infer<typeof presenceMemberLeftSchema>;

export const presenceSyncRequestedSchema = z.object({
    entityType: presenceEntityTypeSchema,
    entityId: z.string().min(1),
    occurredAt: z.string(),
});
export type PresenceSyncRequested = z.infer<typeof presenceSyncRequestedSchema>;

/** A single record reference. */
export interface PresenceRef {
    readonly entityType: PresenceEntityType;
    readonly entityId: string;
}

/** Stable key for a record reference. Matches the API group-key segment ordering. */
export function presenceRefKey(entityType: PresenceEntityType, entityId: string): string {
    return `${entityType}:${entityId}`;
}
